import Point from '@arcgis/core/geometry/Point.js';
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import { getMap, getMapView } from '@/map/mapStore';
import { useLayerRegistry } from '@/map/layers/registry';
import {
  ensureFeatureLayer,
  requireLayerRecord,
  translateAttributes,
} from './shared/layers';
import type { Tool } from './types';

type Input = {
  lon: number;
  lat: number;
  layer_ids?: string[];
  tolerance_pixels?: number;
};

const DEFAULT_TOLERANCE_PIXELS = 8;
const FALLBACK_TOLERANCE_METERS = 25;
const MAX_ATTRIBUTES_PER_FEATURE = 12;
const MAX_HITS_PER_LAYER = 10;

export const identifyAtPoint: Tool<Input> = {
  name: 'identify_at_point',
  description:
    'List the features at a lon/lat across one or more layers (default: all currently-visible feature layers). Returns `[{ layer_id, objectid, attributes, distance_m }]`. Use for "what is at this point?" / "what did I just click?" — not for SQL filtering.',
  input_schema: {
    type: 'object',
    properties: {
      lon: { type: 'number' },
      lat: { type: 'number' },
      layer_ids: { type: 'array', items: { type: 'string' } },
      tolerance_pixels: { type: 'number', exclusiveMinimum: 0 },
    },
    required: ['lon', 'lat'],
    additionalProperties: false,
  },
  execute: async ({ lon, lat, layer_ids, tolerance_pixels }) => {
    const point = new Point({
      x: lon,
      y: lat,
      spatialReference: new SpatialReference({ wkid: 4326 }),
    });

    const view = getMapView();
    const map = getMap();
    const px = tolerance_pixels && tolerance_pixels > 0 ? tolerance_pixels : DEFAULT_TOLERANCE_PIXELS;
    const toleranceMeters =
      view && typeof view.scale === 'number' && view.scale > 0
        ? (view.scale * 0.0254 * px) / 96
        : FALLBACK_TOLERANCE_METERS;

    const records = useLayerRegistry.getState().records;
    let targetIds: string[];
    if (layer_ids && layer_ids.length > 0) {
      targetIds = layer_ids;
    } else {
      targetIds = [];
      for (const r of records) {
        if (!map) break;
        const layer = map.layers.find((l) => l.id === r.id);
        if (layer && layer.visible !== false) targetIds.push(r.id);
      }
    }

    if (targetIds.length === 0) {
      return { hits: [], tolerance_meters: toleranceMeters };
    }

    const perLayer = await Promise.all(
      targetIds.map(async (id) => {
        try {
          const layer = await ensureFeatureLayer(id);
          const probe = requireLayerRecord(id).probe;
          const oidField = layer.objectIdField || 'objectid';
          const rs = await layer.queryFeatures({
            geometry: point,
            distance: toleranceMeters,
            units: 'meters',
            spatialRelationship: 'intersects',
            outFields: ['*'],
            returnGeometry: true,
            num: MAX_HITS_PER_LAYER,
          });
          return rs.features.map((f) => {
            const attrs = translateAttributes(f.attributes ?? {}, probe);
            const trimmed = limitAttributes(attrs);
            const oid = f.attributes?.[oidField];
            const distance =
              f.geometry != null ? safeDistance(point, f.geometry as Geometry) : undefined;
            return {
              layer_id: id,
              objectid: typeof oid === 'number' ? oid : Number(oid ?? 0),
              attributes: trimmed,
              distance_m: distance,
            };
          });
        } catch (err) {
          return [
            {
              layer_id: id,
              error: (err as Error).message ?? 'identify failed',
            },
          ];
        }
      }),
    );

    const hits = perLayer.flat();
    return {
      hits,
      tolerance_meters: toleranceMeters,
      scanned_layers: targetIds,
    };
  },
};

function limitAttributes(attrs: Record<string, unknown>): Record<string, unknown> {
  const entries = Object.entries(attrs).slice(0, MAX_ATTRIBUTES_PER_FEATURE);
  return Object.fromEntries(entries);
}

function safeDistance(a: Geometry, b: Geometry): number | undefined {
  try {
    const d = geometryEngine.distance(
      a as Parameters<typeof geometryEngine.distance>[0],
      b as Parameters<typeof geometryEngine.distance>[1],
      'meters',
    );
    return Number.isFinite(d) ? d : undefined;
  } catch {
    return undefined;
  }
}
